export const APP_SHELL = {
  IOS_NATIVE: "ios_native",
  ANDROID_NATIVE: "android_native",
} as const;

export type AppShell = (typeof APP_SHELL)[keyof typeof APP_SHELL];

const KNOWN_SHELLS = new Set<string>(Object.values(APP_SHELL));

export const isKnownAppShell = (value: unknown): value is AppShell =>
  typeof value === "string" && KNOWN_SHELLS.has(value);

export const parseAppShell = (value: unknown): AppShell | "" =>
  isKnownAppShell(value) ? value : "";

export const isNewNativeAppShell = (shell: string): boolean => isKnownAppShell(shell);

export const isNativeIOSAppShell = (shell: string): boolean => shell === APP_SHELL.IOS_NATIVE;

export const isNativeAndroidAppShell = (shell: string): boolean =>
  shell === APP_SHELL.ANDROID_NATIVE;

/**
 * Read app shell on the client: the shell the native app injects before content loads, falling
 * back to the serialized SSR state. Client-side counterpart to ``appShellFromRequest``, for
 * class components and render-time branches that cannot use the `useAppShell` hook.
 */
export const appShellFromWindow = (): AppShell | "" => {
  if (typeof window === "undefined") return "";
  const injected = (window as { appShell?: unknown }).appShell;
  const ssr = (window as { __INITIAL_STATE__?: { appData?: { appShell?: unknown } } })
    .__INITIAL_STATE__?.appData?.appShell;
  return parseAppShell(injected) || parseAppShell(ssr);
};

/** Read app shell from an Express-like request (header, then cookie, then query). */
export const appShellFromRequest = (req: {
  headers?: Record<string, unknown>;
  cookies?: Record<string, unknown>;
  query?: Record<string, unknown>;
}): AppShell | "" => {
  const raw = req.headers?.["app-shell"] ?? req.cookies?.app_shell ?? req.query?.app_shell ?? "";
  return parseAppShell(Array.isArray(raw) ? raw[0] : raw);
};
